import * as _ from 'lodash';
import { FormEvent } from 'react';
import { IDonorProfileContact, IDonorProfileDonorData, IListItem, IState } from '@nkr/models/index';
import { DonationType, FileDocumentType, MatchStatus } from '@nkr/enums/index';
import { updateProfileDonorData } from './update-profile-donor-data';
import { IValidation } from './validation';
import { Col, ControlLabel, Form, FormControl, FormGroup, Panel, Row } from 'react-bootstrap';
import ReactHtmlParser from 'react-html-parser';
import YesNoUnknown from '../yes-no-unknown';
import Comments from './comments';
import Kits from './kits';
import MaskedInput from 'react-maskedinput';
import RelationshipToRecipient from './relationship-to-recipient';
import Alias from './alias';
import DonorFiles from './donor-files';

interface IProps {
    centerId: number,
    data: IDonorProfileDonorData,
    isSuperAdmin: boolean,
    isCenter: boolean,
    adminComments: string,
    validation: IValidation,
    surgeonList: IDonorProfileContact[],
    coordinatorList: IDonorProfileContact[],
    countryList: IListItem[],
    stateList: IState[],
    declineAdpReasonList: IListItem[],
    relationshipToPatientList: IListItem[],
    remoteEvalStatus: IListItem[],
    rdnCenterList: IListItem[],
    onChange: (data: IDonorProfileDonorData) => void,
    onAdminChange: (value: string) => void,
}

const DonorDataPairedContent = (props: IProps) => {
    const handleSelectChange = (e: FormEvent<FormControl & HTMLSelectElement>) => {
        let name = e.currentTarget.name;
        let value = parseInt(e.currentTarget.value);
        let data = _.cloneDeep(props.data);

        data = updateProfileDonorData.onNumericChange(name, value, data);

        props.onChange(data);
    };

    const handleTextChange = (e: FormEvent<FormControl & HTMLInputElement>) => {
        let name = e.currentTarget.name;
        let value = e.currentTarget.value;
        let data = _.cloneDeep(props.data);

        data = updateProfileDonorData.onTextChange(name, value, data);

        props.onChange(data);
    };

    const handleBooleanChange = (name: string, value: boolean) => {
        let data = _.cloneDeep(props.data);

        data = updateProfileDonorData.onBooleanChange(name, value, data);

        props.onChange(data);
    };

    const handleAdminCommentsChange = (e: FormEvent<FormControl & HTMLInputElement>) => {
        let value = e.currentTarget.value;

        props.onAdminChange(value);
    };

    let canChangeAlias = props.data.canChangeAlias || props.isSuperAdmin;

    let showRdnOption = props.data.donationType == DonationType.StandardVoucher || props.data.donationType == DonationType.DirectedDonor;

    let rdnCenterList = props.rdnCenterList.filter(x => x.id != props.data.centerId);

    return (
        <div className='container-fluid donor-profile-section donor-profile-content'>
            {(props.data.isAdp || (props.data.isDirect && !props.data.isInternalDirect)) &&
                <Row>
                    <Col xs={12} lg={12}>
                        <Panel>
                            <Panel.Heading>Voucher</Panel.Heading>
                            <Panel.Body>
                                <DonorFiles
                                    key={props.data.consentForm ? 'pending' : 'idle'}
                                    donorId={props.data.donorId}
                                    documentType={FileDocumentType.DonorAdp}
                                    pendingFile={props.data.consentForm}
                                    onFileSelected={file => props.onChange({ ...props.data, consentForm: file })}
                                    onFileRemoved={() => props.onChange({ ...props.data, consentForm: null })}
                                    onAppendChanged={append => props.onChange({ ...props.data, appendConsentForm: append })}
                                />
                                <Row>
                                    <Col xs={12} lg={3}>
                                        <label>Voucher Consent Reviewed:</label>
                                    </Col>
                                    <Col xs={12} lg={4}>
                                        <YesNoUnknown
                                            name='voucherConsentsReviewed'
                                            yes={props.data.voucherConsentsReviewed == true}
                                            no={props.data.voucherConsentsReviewed == false}
                                            unknown={props.data.voucherConsentsReviewed == null}
                                            onChange={handleBooleanChange}
                                        />
                                    </Col>
                                </Row>
                            </Panel.Body>
                        </Panel>
                    </Col>
                </Row>
            }

            {/* <span>paired</span> */}
            <Row>
                <Col xs={12} lg={12}>
                    <Form horizontal>
                        <FormGroup>
                            <Col xs={12} lg={2} componentClass={ControlLabel}>Donation Type:</Col>
                            <Col xs={12} lg={7}>
                                <div className='form-control-static'>{props.data.donationTypeDescription}</div>
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={2} componentClass={ControlLabel}>Registration Date:</Col>
                            <Col xs={12} lg={7}>
                                <div className='form-control-static'>{props.data.registrationDate}</div>
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={2} componentClass={ControlLabel}>First Exportable Date:</Col>
                            <Col xs={12} lg={7}>
                                <div className='form-control-static'>{props.data.firstExportableDate != '' ? props.data.firstExportableDate : '- not specified -'}</div>
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={2} componentClass={ControlLabel}>MRN:</Col>
                            <Col xs={12} lg={4}>
                                <FormControl
                                    name='mrn'
                                    maxLength={24}
                                    className='form-control'
                                    value={props.data.mrn}
                                    onChange={handleTextChange} />
                            </Col>

                            <Col xs={12} lg={2} componentClass={ControlLabel}>Hint/Cross Reference:</Col>
                            <Col xs={12} lg={3}>
                                <FormControl
                                    name='crossReferenceHint'
                                    maxLength={32}
                                    className='form-control'
                                    value={props.data.crossReferenceHint}
                                    onChange={handleTextChange} />
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={2} componentClass={ControlLabel}>Donor Surgeon:</Col>
                            <Col xs={12} lg={4}>
                                <FormControl
                                    componentClass='select'
                                    name='surgeonContactId'
                                    value={props.data.surgeonContactId}
                                    onChange={handleSelectChange}
                                >
                                    <option value={0}>- Select -</option>
                                    {props.surgeonList.map(surgeon =>
                                        <option key={'ds-' + surgeon.id} value={surgeon.id}>{surgeon.fullName}</option>
                                    )}
                                </FormControl>
                            </Col>
                        </FormGroup>

                        <RelationshipToRecipient
                            relationshipToRecipient={props.data.relationshipToRecipient}
                            relationshipToPatientOther={props.data.relationshipToPatientOther}
                            relationshipToPatientList={props.relationshipToPatientList}
                            onTextChange={handleTextChange}
                        />

                        {props.data.matchStatus != MatchStatus.Transplanted && (
                            <FormGroup>
                                <Col xs={12} lg={2} componentClass={ControlLabel}>Donor Is Active for Matching:</Col>
                                <Col xs={12} lg={4}>
                                    <YesNoUnknown
                                        name='isActive'
                                        yes={props.data.isActive}
                                        no={!props.data.isActive}
                                        onChange={handleBooleanChange} />
                                </Col>
                            </FormGroup>
                        )}

                        <Alias
                            alias={props.data.alias}
                            allowEdit={canChangeAlias}
                            isValid={props.validation.alias}
                            onChange={handleTextChange}
                        />

                        <FormGroup>
                            <Col xs={12} lg={2} componentClass={ControlLabel}>UNOS Donor ID:</Col>
                            <Col xs={12} lg={4}>
                                <FormControl
                                    name='unosId'
                                    maxLength={7}
                                    className='form-control'
                                    value={props.data.unosId}
                                    onChange={handleTextChange} />
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={2} componentClass={ControlLabel}>Primary Coordinator:</Col>
                            <Col xs={12} lg={4}>
                                <FormControl
                                    componentClass='select'
                                    name='coordinatorId'
                                    value={props.data.coordinatorId}
                                    onChange={handleSelectChange}
                                >
                                    <option value={0}>Default Coordinator</option>
                                    {props.coordinatorList.map(coordinator =>
                                        <option key={'dc-' + coordinator.id} value={coordinator.id}>{coordinator.fullName}</option>
                                    )}
                                </FormControl>
                            </Col>
                        </FormGroup>

                        {props.data.isRemotePhlebo && (
                            <FormGroup>
                                <Col xs={12} lg={2} componentClass={ControlLabel}>Use Traveling Phlebotomist:</Col>
                                <Col xs={12} lg={4}>
                                    <YesNoUnknown
                                        name='usePhlebotomy'
                                        yes={props.data.usePhlebotomy}
                                        no={!props.data.usePhlebotomy}
                                        onChange={handleBooleanChange} />
                                </Col>
                            </FormGroup>
                        )}

                        {props.centerId == props.data.centerId ?
                            <>
                                {showRdnOption && (
                                    <FormGroup>
                                        <Col xs={12} lg={2} componentClass={ControlLabel}>RDN Managing Center:</Col>
                                        <Col xs={12} lg={4}>
                                            <FormControl
                                                componentClass='select'
                                                name='rdnCenterId'
                                                value={props.data.rdnCenterId}
                                                onChange={handleSelectChange}
                                            >
                                                <option value={0}>- Not a Remote Donor -</option>
                                                {rdnCenterList.map(item =>
                                                    <option key={'rdn-ctr-' + item.id} value={item.id}>{item.description}</option>
                                                )}
                                            </FormControl>
                                        </Col>
                                    </FormGroup>
                                )}
                            </>
                            :
                            <>
                                {props.data.remoteWorkupBilledDate == '' ?
                                    <FormGroup>
                                        <Col xs={12} lg={2} componentClass={ControlLabel}>Workup Completed Date:</Col>
                                        <Col xs={12} lg={4}>
                                            <MaskedInput
                                                name='remoteWorkupDate'
                                                className='form-control'
                                                mask='11/11/1111'
                                                placeholder='mm/dd/yyyy'
                                                value={props.data.remoteWorkupDate}
                                                onChange={handleTextChange}
                                            />
                                        </Col>
                                    </FormGroup>
                                    :
                                    <FormGroup>
                                        <Col xs={12} lg={2} componentClass={ControlLabel}>Workup Completed Date:</Col>
                                        <Col xs={12} lg={4}>
                                            <div className='form-control-static'>{props.data.remoteWorkupDate}</div>
                                        </Col>
                                    </FormGroup>
                                }
                            </>
                        }
                    </Form>

                    <br />

                    <Kits
                        directCryoSend={props.data.directCryoSend}
                        directXmSend={props.data.directXmSend}
                        directSerologySend={props.data.directSerologySend}
                        onBooleanChange={handleBooleanChange}
                    />
                </Col>
            </Row >

            {props.isCenter &&
                <>
                    {props.data.comments != '' &&
                        <Row>
                            <Col xs={12} lg={12}>
                                {ReactHtmlParser(props.data.comments)}
                            </Col>
                        </Row>
                    }

                    <Row>
                        <Col xs={12} lg={12}>
                            <Comments
                                adminComments={props.adminComments}
                                centerId={props.centerId}
                                donorId={props.data.donorId}
                                onChange={handleAdminCommentsChange}
                            />
                        </Col>
                    </Row >
                </>
            }

        </div >
    );
}

export default DonorDataPairedContent;